
import React, { useEffect } from 'react'
import { useTenantInformation } from './useTenantInformation'
import { useAuthFlowMap } from './AuthenticatorProviders/useAuthFlowMap'
import { useIsMutating } from '@tanstack/react-query'
import LoadingPageV2 from '../LoadingPageV2'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { useDelayFalse } from '../../utils/delayFalse'
import { LoginHeader } from './AuthenticatorProviders/CognitoAuthenticator/LoginHeader'
import { EmailInputForm } from './AuthenticatorProviders/CognitoAuthenticator/EmailInputForm'
import './style.css'
import { TenantListField } from './TenantListField'
import { useAuthenticatedContext } from '../../hooks/AuthenticatedContext'
import { useNavigate } from 'react-router-dom'
import ROUTES from '../../consts/paths'
import { useAppSearchParams } from '../../hooks/useAppSearchParams'
import { LoginShell } from './LoginShell'

// • Login wrapper that decides which auth flow to render (Cognito SSO, Edlink SSO, or manual/local)
// • Fetches tenant info from email/tenant selection, then maps tenant login method to the correct auth provider component
// • Handles fallback UX: show email form → show tenant list on error/manual → render resolved auth component

export const LoginWrapper: React.FC = () => {
  // • Tracks whether Edlink SSO mutation is currently running (used to show global loading UI)
  const isEdlinkSsoInFlight = useIsMutating({ mutationKey: ['edlinkSso'] }) > 0
  const [openSnackbar, setOpenSnackbar] = React.useState(false)
  const [selectedTenantKey, setSelectedTenantKey] = React.useState('')
  const { isAuthed } = useAuthenticatedContext()
  const navigate = useNavigate()
  const { searchParams } = useAppSearchParams()

  // • Tracks Cognito UI "pending" state and delays turning it off to avoid flicker
  const { isPending: isCognitoSsoInFlightRaw } = useAuthenticator((context) => [context.isPending])
  const isCognitoSsoInFlight = useDelayFalse(isCognitoSsoInFlightRaw, 1300)
  const [isManualLogin, setIsManualLogin] = React.useState(false)
  const [isShowTenantList, setIsShowTenantList] = React.useState(false)
  const codeKeyFromEdlink = searchParams.get('code')

  const { email, isTenantInfoPending, tenantErrorMessage, tenantInfo, setEmail, getTenantInformation, resetTenantInformation } = useTenantInformation()

  // • useAuthFlowMap returns a mapping of provider components + a resolved method based on email/tenant settings
  // • Final component chooses manual/local override if user switches to manual flow
  const loginMethod = tenantInfo?.default_login_method || 'undefined'
  const { authProvidersMap, loginMethod: resolvedLoginMethod } = useAuthFlowMap({ email, loginMethod, handleBack })
  const activeLoginMethod = isManualLogin ? 'local' : resolvedLoginMethod
  const Component = authProvidersMap[activeLoginMethod]

  const handleSubmit = async (): Promise<void> => {
    if (openSnackbar || isManualLogin) {
      setIsShowTenantList(true)
      return
    }
    void getTenantInformation(selectedTenantKey)
  }

  const submitTenantInfo = async (): Promise<void> => {
    void getTenantInformation(selectedTenantKey)
  }

  // • "Back" resets the entire flow to initial state (clears tenant info + UI state)
  function handleBack (): void {
    resetTenantInformation()
    setIsManualLogin(false)
    setIsShowTenantList(false)
    setSelectedTenantKey('')
  }

  // • If tenant lookup fails, automatically switch UI to tenant selection list (fallback path)
  useEffect(() => {
    if (tenantErrorMessage) {
      setIsShowTenantList(true)
    }
  }, [tenantErrorMessage, resetTenantInformation])

  // • Global loading gate while any SSO flow is in-flight
  if (isEdlinkSsoInFlight || isCognitoSsoInFlight) return <LoadingPageV2 />
  // • UI state machine:
  //   - Email input: no tenantInfo yet AND tenant list not being shown
  //   - Tenant list: tenant list is shown AND tenantInfo not resolved yet
  //   - Auth component: tenantInfo resolved (render chosen provider component)
  const showEmailInput = !tenantInfo && !isShowTenantList
  const isUserUsingManualFlowWithValidTenant = tenantInfo
  const showTenantListInput = isShowTenantList && !tenantInfo

  if (isAuthed && !codeKeyFromEdlink) {
    navigate(ROUTES.HOME)
    return <></>
  }

  return (
    <LoginShell>
      {showEmailInput && (
        <>
          <LoginHeader />
          <EmailInputForm
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            email={email}
            setEmail={setEmail}
            setManualLogin={() => {
              setIsManualLogin(true)
              setIsShowTenantList(true)
            }}
            isPending={isTenantInfoPending}
            onSubmit={() => { void handleSubmit() }}
          />
        </>
      )}

      {showTenantListInput && (
        <TenantListField
          handleSubmit={() => { void submitTenantInfo() }}
          handleBack={handleBack}
          selectedTenantKey={selectedTenantKey}
          setSelectedTenantKey={setSelectedTenantKey}
          isLoading={isTenantInfoPending}
          email={email}
        />
      )}

      {isUserUsingManualFlowWithValidTenant && <Component />}
    </LoginShell>
  )
}
